// Installs small global guards to avoid noisy runtime errors.
// Focus: prevent Performance.measure() from throwing when marks are missing
// (e.g. "The mark 'blth:start' does not exist.") and dedupe repeated errors.
/* eslint-disable no-console */

function tryOverride(obj, key, value) {
  try {
    // eslint-disable-next-line no-param-reassign
    obj[key] = value;
    return true;
  } catch (_) {
    // ignore
  }
  try {
    Object.defineProperty(obj, key, {
      value,
      configurable: true,
      writable: true,
    });
    return true;
  } catch (_) {
    return false;
  }
}

function isMissingPerformanceMarkError(err) {
  const msg = String(err && err.message ? err.message : err);
  return (
    msg.includes("Failed to execute 'measure' on 'Performance'") &&
    msg.includes("does not exist")
  );
}

export function installPerformanceMeasureGuard() {
  if (typeof window === "undefined") return;
  if (!window.performance || typeof window.performance.measure !== "function") return;

  const perf = window.performance;
  const original = perf.measure.bind(perf);

  function guardedMeasure(...args) {
    try {
      return original(...args);
    } catch (err) {
      // Some third-party libs call performance.measure with missing marks.
      // That throws and can spam the app. Silently ignore this specific case.
      if (isMissingPerformanceMarkError(err)) return undefined;
      throw err;
    }
  }

  // Override instance method (most calls use `performance.measure(...)`).
  tryOverride(perf, "measure", guardedMeasure);

  // Also try overriding prototype for extra safety.
  if (typeof window.Performance !== "undefined" && window.Performance.prototype) {
    const proto = window.Performance.prototype;
    if (typeof proto.measure === "function") {
      const originalProto = proto.measure;
      function guardedProtoMeasure(...args) {
        try {
          return originalProto.apply(this, args);
        } catch (err) {
          if (isMissingPerformanceMarkError(err)) return undefined;
          throw err;
        }
      }
      tryOverride(proto, "measure", guardedProtoMeasure);
    }
  }
}

function defaultErrorKey({ type, message, stack }) {
  const s = stack ? String(stack).slice(0, 500) : "";
  return `${type}|${String(message).slice(0, 300)}|${s}`;
}

export function installGlobalErrorDeduper({
  windowMs = 4000,
  maxEntries = 300,
  suppressConsole = false,
} = {}) {
  if (typeof window === "undefined") return;

  const seen = new Map(); // key -> timestamp

  function cleanup(now) {
    if (seen.size <= maxEntries) return;
    for (const [k, ts] of seen) {
      if (now - ts > windowMs) seen.delete(k);
    }
    if (seen.size > maxEntries) seen.clear();
  }

  function shouldSuppress({ type, message, stack }) {
    // Always suppress known noisy perf mark errors.
    if (
      String(message).includes("Failed to execute 'measure' on 'Performance'") &&
      String(message).includes("does not exist")
    ) {
      return true;
    }

    const now = Date.now();
    cleanup(now);

    const key = defaultErrorKey({ type, message, stack });
    const last = seen.get(key);
    if (last && now - last < windowMs) return true;
    seen.set(key, now);
    return false;
  }

  window.addEventListener(
    "error",
    (event) => {
      const err = event && event.error;
      const message = (event && event.message) || (err && err.message) || "Unknown error";
      const stack = err && err.stack;

      if (shouldSuppress({ type: "error", message, stack })) {
        // Prevent default browser logging for repeated/noisy cases.
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        return;
      }

      if (!suppressConsole) {
        console.error("[global:error]", message, err || event);
      }
    },
    true
  );

  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event && event.reason;
      const message =
        (reason && reason.message) ||
        (typeof reason === "string" ? reason : null) ||
        "Unhandled promise rejection";
      const stack = reason && reason.stack;

      if (shouldSuppress({ type: "unhandledrejection", message, stack })) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        return;
      }

      if (!suppressConsole) {
        console.error("[global:unhandledrejection]", message, reason);
      }
    },
    true
  );
}

export function installGlobalGuards() {
  installPerformanceMeasureGuard();
  installGlobalErrorDeduper();
}

